<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <div>
          <h1 class="sci-page-title-only">Autorizações de Abastecimento</h1>
          <p class="text-sm text-slate-500 font-medium mt-1">Gerencie e visualize as autorizações do sistema</p>
        </div>
      </div>

      <!-- FILTROS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-filter-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  FILTROS DE BUSCA
              </h2>
          </div>
          <div class="p-8">
              <v-row dense>
                  <v-col cols="12" sm="12" md="3">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Inicial</label>
                    <Date @dateValue="[(filter.startDate = $event)]" />
                  </v-col>
                  <v-col cols="12" sm="12" md="3">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Final</label>
                    <Date @dateValue="[(filter.endDate = $event)]" />
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
                    <v-autocomplete
                      :disabled="userGroup == 1 ? false : true"
                      :items="prefectures"
                      v-model="filter.prefecture_id"
                      item-value="prefecture_id"
                      item-text="prefecture_social_name"
                      @change="[getVehicles(filter.prefecture_id), getDrivers(filter.prefecture_id), getProviders(filter.prefecture_id)]"
                      placeholder="Selecione a prefeitura"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="12" md="4">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Fornecedor</label>
                    <v-autocomplete
                      :items="providers"
                      item-value="provider_id"
                      item-text="provider_name"
                      v-model="filter.provider_id"
                      placeholder="Selecione o fornecedor"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="12" md="4">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Veículo</label>
                    <v-autocomplete
                      :items="vehicles"
                      v-model="filter.vehicle_id"
                      item-value="vehicle_id"
                      item-text="vehicle_board"
                      placeholder="Selecione o veículo"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                      clearable
                    >
                      <template slot="selection" slot-scope="{ item }">
                        <span class="text-sm">{{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}</span>
                      </template>
                      <template slot="item" slot-scope="{ item }">
                        <span class="text-sm">{{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="12" md="4">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Motorista</label>
                    <v-autocomplete
                      :items="drivers"
                      item-value="driver_id"
                      item-text="driver_name"
                      v-model="filter.driver_id"
                      placeholder="Selecione o motorista"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                      clearable
                    />
                  </v-col>
              </v-row>

              <div class="flex justify-end gap-3 mt-8">
                  <button 
                    @click="loadAbastecimento"
                    :disabled="!(filter.prefecture_id && filter.provider_id && filter.startDate && filter.endDate) || loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/20 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-10 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-bold uppercase tracking-widest"
                  >
                      <v-icon color="white" small>mdi-filter-variant</v-icon>
                      {{ loader ? 'FILTRANDO...' : 'FILTRAR RESULTADOS' }}
                  </button>
              </div>
          </div>
      </section>

      <!-- RESULTADOS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
            <div class="flex items-center gap-3">
                <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                    LISTAGEM DE AUTORIZAÇÕES
                </h2>
            </div>
            <div class="flex items-center gap-1 w-full max-w-sm justify-end">
                <v-tooltip bottom color="slate-800">
                    <template v-slot:activator="{ on, attrs }">
                        <button 
                            v-bind="attrs" v-on="on" @click="loadAbastecimento"
                            class="p-2 rounded-lg text-slate-400 hover:bg-slate-600 hover:text-white transition-colors"
                        >
                            <v-icon color="currentColor">mdi-history</v-icon>
                        </button>
                    </template>
                    <span class="text-[10px] font-bold uppercase tracking-widest">Recarregar</span>
                </v-tooltip>
                
                <div class="h-6 border-r border-slate-500 mx-2"></div>

                <div class="relative w-full max-w-xs">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <v-icon small color="grey">mdi-magnify</v-icon>
                    </div>
                    <input 
                        v-model="search"
                        placeholder="PESQUISAR..."
                        class="w-full bg-white text-slate-800 text-[10px] font-bold uppercase tracking-widest rounded-lg py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all placeholder:text-slate-400 shadow-sm"
                    >
                </div>
            </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="data"
          :search="search"
          :items-per-page="itemsPerPage"
          :loading="loader"
          loading-text="CARREGANDO..."
          class="elevation-0"
          hide-default-footer
          :page.sync="page"
          @page-count="pageCount = $event"
        >
          <template v-slot:item.datehour="{ item }">
            <span class="text-xs font-semibold text-slate-600">{{ convertDateTime(item.datehour) }}</span>
          </template>
          <template v-slot:item.model_desc="{ item }">
            <span v-if="item.vehicle_id" class="text-xs font-medium text-slate-700">{{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}</span>
            <span v-else class="text-xs italic text-slate-400">Não definido</span>
          </template>
          <template v-slot:item.authorizer="{ item }">
            <span class="text-[10px] font-bold text-slate-600 uppercase">{{ item.is_autorized ? item.authorizer : 'Senha do Veículo' }}</span>
          </template>
          <template v-slot:item.supply_liters_stocked="{ item }">
            <span class="font-bold text-slate-700">{{ realMaskII(Number(item.supply_liters_stocked || 0).toFixed(3)) }}</span>
          </template>
          <template v-slot:item.supply_authorized_value="{ item }">
            <span class="text-emerald-600 font-bold">R$ {{ realMask(Number(item.supply_authorized_value || 0).toFixed(2)) }}</span>
          </template>
          <template v-slot:item.supply_value="{ item }">
            <span class="font-black text-slate-800">
              R$ {{ 
                realMask(Number(item.supply_value || (Number(item.supply_liters_stocked) * (Number(item.supply_price_day) || Number(item.supply_bidding_price))) || 0).toFixed(2)) 
              }}
            </span>
          </template>
          <template v-slot:item.supply_authorized_liters="{ item }">
            <span class="font-bold text-slate-700">{{ realMaskII(Number(item.supply_authorized_liters || 0).toFixed(3)) }}</span>
          </template>
          <template v-slot:item.last_km="{ item }">
            <span class="text-xs font-semibold text-slate-600">{{ item.last_km || 0 }}</span>
          </template>
          <template v-slot:item.supply_km="{ item }">
            <span class="text-xs font-semibold text-slate-800">{{ item.supply_km || 0 }}</span>
          </template>
          <template v-slot:item.average="{ item }">

            <span class="text-xs font-bold text-slate-500 whitespace-nowrap">
              {{
                item.supply_liters_stocked > 0 
                ? `${((item.supply_km - item.last_km) / item.supply_liters_stocked).toFixed(2).toLocaleString("pt-BR")} KM/L`
                : '0 KM/L'
              }}
            </span>
          </template>
          <template v-slot:item.action="{ item }">
            <v-menu offset-y transition="scale-transition">
              <template v-slot:activator="{ on, attrs }">
                <button v-bind="attrs" v-on="on" class="p-1 rounded-md hover:bg-slate-100 transition-colors">
                  <v-icon small>mdi-dots-vertical</v-icon>
                </button>
              </template>

              <v-list dense class="py-1">
                <v-list-item @click="abrirHistorico(item)" class="px-3 hover:bg-slate-50">
                  <v-icon small class="mr-2" color="slate-600">mdi-card-text-outline</v-icon>
                  <v-list-item-title class="text-[11px] font-bold uppercase tracking-wider">Histórico</v-list-item-title>
                </v-list-item>

                <v-list-item @click="editar(item)" :disabled="item.supply_status === 'CANCELADO'" class="px-3 hover:bg-slate-50">
                  <v-icon small class="mr-2" color="blue">mdi-pencil-outline</v-icon>
                  <v-list-item-title class="text-[11px] font-bold uppercase tracking-wider">Editar</v-list-item-title>
                </v-list-item>

                <v-list-item @click="cancelar(item)" :disabled="item.supply_status === 'CANCELADO' || item.supply_status === 'CONCLUIDO'" class="px-3 hover:bg-slate-50">
                  <v-icon small class="mr-2" color="rose">mdi-cancel</v-icon>
                  <v-list-item-title class="text-[11px] font-bold uppercase tracking-wider text-rose-500">Cancelar</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>

         <!-- Custom Pagination Footer -->
         <div class="bg-slate-50 px-6 py-4 border-t border-slate-200 flex flex-column md:flex-row justify-between items-center gap-4 text-xs text-slate-500 font-medium w-full">
            <span>Exibindo {{ paginationStart }} de {{ data.length }} registros</span>
            
            <div class="flex gap-1">
                <button 
                  @click="page > 1 ? page-- : null" 
                  :disabled="page === 1"
                  class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                
                <button 
                  v-for="p in visiblePages" 
                  :key="p"
                  @click="page = p"
                  :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                  class="px-3 py-1.5 border rounded-md transition-colors font-medium"
                >
                  {{ p }}
                </button>

                <button 
                  @click="page < pageCount ? page++ : null" 
                  :disabled="page === pageCount"
                  class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Próximo
                </button>
            </div>
         </div>
      </section>
    </div>

    <!-- MODAL HISTÓRICO -->
    <v-dialog v-model="showHistoryModal" max-width="900px" scrollable transition="dialog-bottom-transition">
      <div class="bg-slate-50 rounded-xl overflow-hidden shadow-2xl font-sans flex flex-col max-h-[90vh]">
        <!-- Header -->
        <div class="px-6 py-4 flex justify-between items-center shrink-0 shadow-sm z-10" style="background-color: #333333;">
          <div class="flex flex-col">
            <h3 class="text-white font-bold uppercase tracking-widest text-sm flex items-center gap-2">
              <v-icon color="orange" small>mdi-history</v-icon>
              DETALHES DO ABASTECIMENTO
            </h3>
            <span class="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">CÓDIGO: {{ selectedHistoryItem.supply_code || '---' }}</span>
          </div>
          <button @click="showHistoryModal = false" class="text-slate-400 hover:text-white transition-colors rounded-full p-1 hover:bg-slate-700">
            <v-icon small color="inherit">mdi-close</v-icon>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6 overflow-y-auto bg-slate-50/50">
          <div class="space-y-6">
            
            <!-- SEÇÃO: INFORMAÇÕES BÁSICAS -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <label class="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">VEÍCULO</label>
                <div class="text-xs font-bold text-slate-800 leading-tight">
                  {{ selectedHistoryItem.brand_desc }} {{ selectedHistoryItem.model_desc }}
                </div>
                <div class="text-[10px] font-medium text-slate-500 mt-1 uppercase tracking-wider">
                  PLACA: {{ selectedHistoryItem.vehicle_board }}
                </div>
              </div>
              
              <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <label class="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">MOTORISTA</label>
                <div class="text-xs font-bold text-slate-800 leading-tight">
                  {{ selectedHistoryItem.driver_name || 'NÃO INFORMADO' }}
                </div>
                <div class="text-[10px] font-medium text-slate-500 mt-1 uppercase tracking-wider">
                  DATA: {{ convertDateTime(selectedHistoryItem.datehour) }}
                </div>
              </div>

              <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <label class="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">FORNECEDOR</label>
                <div class="text-xs font-bold text-slate-800 leading-tight">
                  {{ selectedHistoryItem.provider_name }}
                </div>
                <div class="text-[10px] font-bold text-emerald-600 mt-1 uppercase tracking-wider">
                  ITEM: {{ selectedHistoryItem.item_description_desc }}
                </div>
              </div>
            </div>

            <!-- SEÇÃO: VALORES E LITROS -->
            <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div class="px-4 py-2 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
                <v-icon small color="slate-500">mdi-cash-register</v-icon>
                <span class="text-[10px] font-black text-slate-500 uppercase tracking-widest">PRODUÇÃO E FATURAMENTO</span>
              </div>
              <div class="p-4 grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <label class="block text-[9px] font-bold text-slate-400 uppercase mb-1">Litros Autorizados</label>
                  <div class="text-sm font-black text-slate-700">{{ realMaskII(Number(selectedHistoryItem.supply_authorized_liters || 0).toFixed(3)) }}</div>
                </div>
                <div>
                  <label class="block text-[9px] font-bold text-slate-400 uppercase mb-1">Valor Autorizado</label>
                  <div class="text-sm font-black text-slate-700">R$ {{ realMask(Number(selectedHistoryItem.supply_authorized_value || 0).toFixed(2)) }}</div>
                </div>
                <div class="bg-orange-50 -m-2 p-2 rounded-lg border border-orange-100">
                  <label class="block text-[9px] font-bold text-orange-400 uppercase mb-1">Litros Abastecidos</label>
                  <div class="text-sm font-black text-orange-600">{{ realMaskII(Number(selectedHistoryItem.supply_liters_stocked || 0).toFixed(3)) }}</div>
                </div>
                <div class="bg-emerald-50 -m-2 p-2 rounded-lg border border-emerald-100">
                  <label class="block text-[9px] font-bold text-emerald-400 uppercase mb-1">Valor Total</label>
                  <div class="text-sm font-black text-emerald-600">
                    R$ {{ 
                      realMask(Number(selectedHistoryItem.supply_value || (Number(selectedHistoryItem.supply_liters_stocked) * (Number(selectedHistoryItem.supply_price_day) || Number(selectedHistoryItem.supply_bidding_price))) || 0).toFixed(2)) 
                    }}
                  </div>
                </div>
              </div>
              <div class="px-4 py-3 bg-slate-50 border-t border-slate-200 flex justify-between">
                <div class="flex gap-6">
                  <div>
                    <span class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Preço Licitado: </span>
                    <span class="text-[11px] font-bold text-slate-600">R$ {{ realMaskII(Number(selectedHistoryItem.supply_bidding_price || 0).toFixed(3)) }}</span>
                  </div>
                </div>
                <div>
                  <span class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Cupom: </span>
                  <span class="text-[11px] font-black text-slate-800 tracking-widest">{{ selectedHistoryItem.supply_coupon || '---' }}</span>
                </div>
              </div>
            </div>

            <!-- SEÇÃO: QUILOMETRAGEM -->
            <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div class="px-4 py-2 bg-slate-100 border-b border-slate-200 flex justify-between items-center">
                <div class="flex items-center gap-2">
                  <v-icon small color="slate-500">mdi-speedometer</v-icon>
                  <span class="text-[10px] font-black text-slate-500 uppercase tracking-widest">DESEMPENHO E QUILOMETRAGEM</span>
                </div>
                <div class="bg-slate-800 px-3 py-1 rounded-full shadow-lg border border-slate-600">
                   <span class="text-[10px] font-black text-white uppercase tracking-widest">MÉDIA: </span>
                   <span class="text-xs font-black text-orange-400 ml-1">
                      {{
                        selectedHistoryItem.supply_liters_stocked > 0 
                        ? `${((selectedHistoryItem.supply_km - selectedHistoryItem.last_km) / selectedHistoryItem.supply_liters_stocked).toFixed(2).toLocaleString("pt-BR")} KM/L`
                        : '0 KM/L'
                      }}
                   </span>
                </div>
              </div>
              <div class="p-4 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="flex flex-col items-center justify-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <label class="text-[10px] font-bold text-slate-400 uppercase mb-1">Km Anterior</label>
                  <div class="text-lg font-black text-slate-600">{{ selectedHistoryItem.last_km || 0 }}</div>
                </div>
                <div class="flex flex-col items-center justify-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <label class="text-[10px] font-bold text-slate-400 uppercase mb-1">Km Atual</label>
                  <div class="text-lg font-black text-slate-800">{{ selectedHistoryItem.supply_km || 0 }}</div>
                </div>
                <div class="flex flex-col items-center justify-center p-3 bg-orange-50 rounded-lg border border-orange-100">
                  <label class="text-[10px] font-bold text-orange-400 uppercase mb-1">Km Percorrido</label>
                  <div class="text-lg font-black text-orange-600">{{ (selectedHistoryItem.supply_km - selectedHistoryItem.last_km) || 0 }}</div>
                </div>
              </div>
            </div>

            <!-- SEÇÃO: FOTOS -->
            <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div class="px-4 py-2 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
                <v-icon small color="slate-500">mdi-camera</v-icon>
                <span class="text-[10px] font-black text-slate-500 uppercase tracking-widest">REGISTROS FOTOGRÁFICOS</span>
              </div>
              <div class="p-4">
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <!-- Foto Hodômetro -->
                  <div class="space-y-2">
                    <label class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">HODÔMETRO</label>
                    <div class="aspect-video bg-slate-100 rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" @click="abrirFoto(selectedHistoryItem.foto_km)">
                      <img v-if="selectedHistoryItem.foto_km" :src="getUrlFoto(selectedHistoryItem.foto_km)" class="w-full h-full object-cover transition-transform group-hover:scale-110" />
                      <div v-else class="w-full h-full flex items-center justify-center text-slate-300">
                        <v-icon large>mdi-camera-off</v-icon>
                      </div>
                      <div v-if="selectedHistoryItem.foto_km" class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <v-icon color="white">mdi-magnify-plus</v-icon>
                      </div>
                    </div>
                  </div>

                  <!-- Foto Bomba -->
                   <div class="space-y-2">
                    <label class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">BOMBA</label>
                    <div class="aspect-video bg-slate-100 rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" @click="abrirFoto(selectedHistoryItem.foto_bomba)">
                      <img v-if="selectedHistoryItem.foto_bomba" :src="getUrlFoto(selectedHistoryItem.foto_bomba)" class="w-full h-full object-cover transition-transform group-hover:scale-110" />
                      <div v-else class="w-full h-full flex items-center justify-center text-slate-300">
                        <v-icon large>mdi-camera-off</v-icon>
                      </div>
                      <div v-if="selectedHistoryItem.foto_bomba" class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <v-icon color="white">mdi-magnify-plus</v-icon>
                      </div>
                    </div>
                  </div>

                  <!-- Foto Cupom -->
                  <div class="space-y-2">
                    <label class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">CUPOM FISCAL</label>
                    <div class="aspect-video bg-slate-100 rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" @click="abrirFoto(selectedHistoryItem.foto_cupom)">
                      <img v-if="selectedHistoryItem.foto_cupom" :src="getUrlFoto(selectedHistoryItem.foto_cupom)" class="w-full h-full object-cover transition-transform group-hover:scale-110" />
                      <div v-else class="w-full h-full flex items-center justify-center text-slate-300">
                        <v-icon large>mdi-camera-off</v-icon>
                      </div>
                      <div v-if="selectedHistoryItem.foto_cupom" class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <v-icon color="white">mdi-magnify-plus</v-icon>
                      </div>
                    </div>
                  </div>

                  <!-- Foto Motorista -->
                  <div class="space-y-2">
                    <label class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">MOTORISTA</label>
                    <div class="aspect-video bg-slate-100 rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" @click="abrirFoto(selectedHistoryItem.foto_motorista || selectedHistoryItem.foto_confirmacao)">
                      <img v-if="selectedHistoryItem.foto_motorista || selectedHistoryItem.foto_confirmacao" :src="getUrlFoto(selectedHistoryItem.foto_motorista || selectedHistoryItem.foto_confirmacao)" class="w-full h-full object-cover transition-transform group-hover:scale-110" />
                      <div v-else class="w-full h-full flex items-center justify-center text-slate-300">
                        <v-icon large>mdi-camera-off</v-icon>
                      </div>
                      <div v-if="selectedHistoryItem.foto_motorista || selectedHistoryItem.foto_confirmacao" class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <v-icon color="white">mdi-magnify-plus</v-icon>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <div class="bg-white px-6 py-4 flex justify-end gap-3 border-t border-slate-200 shrink-0">
          <button 
            @click="showHistoryModal = false" 
            class="px-8 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white font-bold uppercase tracking-widest text-[11px] shadow-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0"
          >
            FECHAR DETALHES
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- MODAL EDITAR -->
    <v-dialog v-model="showModal" max-width="800px" persistent scrollable>
      <div class="bg-white rounded-xl overflow-hidden shadow-2xl font-sans flex flex-col max-h-[90vh]">
        <!-- Header -->
        <div class="px-6 py-4 flex justify-between items-center shrink-0" style="background-color: #333333;">
          <h3 class="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2">
            <v-icon color="orange" small>mdi-pencil-box-outline</v-icon>
            ALTERAR ABASTECIMENTO
          </h3>
          <button @click="[showModal = false, clearForm()]" class="text-slate-400 hover:text-white transition-colors rounded-full p-1 hover:bg-slate-700">
            <v-icon small color="inherit">mdi-close</v-icon>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6 overflow-y-auto">
          <v-form ref="validForm" v-if="!loadingModal" v-model="validForm" lazy-validation>
            <div class="space-y-6">
              
              <!-- Data e Hora -->
              <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Data e Hora do Abastecimento</label>
                <v-text-field
                  v-mask="'##/##/#### ##:##'"
                  v-model="dataForm.datehour"
                  outlined dense hide-details
                  class="bg-white rounded-lg shadow-sm"
                  prepend-inner-icon="mdi-calendar-clock"
                  :rules="fieldRules"
                />
              </div>

              <!-- Veículo e Fornecedor -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Veículo</label>
                  <v-autocomplete
                    :rules="fieldRules"
                    :items="vehicles_form"
                    item-value="vehicle_id"
                    item-text="vehicle_board"
                    v-model="dataForm.vehicle_id"
                    placeholder="Selecione o veículo"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm"
                    @change="loadItems(dataForm.provider_id, dataForm.vehicle_id, dataForm.prefecture_id)"
                  >
                    <template slot="selection" slot-scope="{ item }">
                      <span class="text-xs font-bold">{{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}</span>
                    </template>
                  </v-autocomplete>
                </div>

                <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Fornecedor</label>
                  <v-autocomplete
                    :rules="fieldRules"
                    :items="providers_form"
                    @change="loadItems(dataForm.provider_id, dataForm.vehicle_id, dataForm.prefecture_id)"
                    item-value="provider_id"
                    item-text="provider_name"
                    v-model="dataForm.provider_id"
                    placeholder="Selecione o fornecedor"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm"
                  />
                </div>
              </div>

              <!-- Motorista e Item -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Motorista</label>
                  <v-autocomplete
                    :items="drivers_form"
                    item-value="driver_id"
                    item-text="driver_name"
                    v-model="dataForm.driver_id"
                    :rules="fieldRules"
                    placeholder="Selecione o motorista"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm"
                  />
                </div>

                <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Item/Combustível</label>
                  <v-autocomplete
                    :rules="fieldRules"
                    :items="items_form"
                    item-value="item_id"
                    item-text="item_name"
                    v-model="dataForm.item_id"
                    placeholder="Selecione o item"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm"
                  >
                    <template slot="selection" slot-scope="{ item }">
                      <span class="text-xs font-bold">{{ item.item_description_desc }}</span>
                    </template>
                    <template slot="item" slot-scope="{ item }">
                      <div class="flex items-center justify-between w-full py-1">
                        <span class="text-xs font-bold">{{ item.item_description_desc }}</span>
                        <span class="text-[10px] font-bold text-emerald-600">R$ {{ realMask(item.item_price.toFixed(2)) }}</span>
                      </div>
                    </template>
                  </v-autocomplete>
                </div>
              </div>

              <!-- Autorização (Opcional) -->
              <div v-if="dataForm.supply_status !== 'CONCLUIDO'" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                  <label class="block text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">Litros Autorizados</label>
                  <v-text-field
                    v-model="dataForm.supply_authorized_liters"
                    v-mask-decimal.br="3"
                    :disabled="disableLitros"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm font-bold text-center"
                  />
                </div>
                <div class="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                  <label class="block text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">Valor Autorizado (R$)</label>
                  <v-text-field
                    v-model="dataForm.supply_authorized_value"
                    v-mask-decimal.br="2"
                    :disabled="disableValor"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm font-bold text-center text-indigo-700"
                  />
                </div>
              </div>

              <!-- Detalhes do Abastecimento Concluído -->
              <div v-if="dataForm.supply_status === 'CONCLUIDO'" class="space-y-6">
                <!-- Valores e Litros -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Lts Abastecidos</label>
                    <v-text-field
                      v-model="dataForm.supply_liters_stocked"
                      v-mask-decimal.br="3"
                      @input="calculateTotalsForm"
                      outlined dense hide-details
                      class="bg-white rounded-lg shadow-sm font-bold text-center"
                    />
                  </div>
                  <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Preço do Dia</label>
                    <v-text-field
                      v-model="dataForm.supply_price_day"
                      v-mask-decimal.br="3"
                      @input="calculateTotalsForm"
                      outlined dense hide-details
                      class="bg-white rounded-lg shadow-sm font-bold text-center text-emerald-600"
                    />
                  </div>
                  <div class="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                    <label class="block text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">Valor Total</label>
                    <v-text-field
                      readonly
                      :value="dataForm.supply_value"
                      v-mask-decimal.br="2"
                      outlined dense hide-details
                      class="bg-white rounded-lg shadow-sm font-black text-center text-indigo-700"
                      prefix="R$"
                    />
                  </div>
                </div>

                <!-- Cupom e KM -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Número do Cupom</label>
                    <v-text-field
                      v-model="dataForm.supply_coupon"
                      outlined dense hide-details
                      class="bg-white rounded-lg shadow-sm font-bold"
                    />
                  </div>
                  <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Quilometragem Atual</label>
                    <v-text-field
                      v-model="dataForm.supply_km"
                      outlined dense hide-details
                      class="bg-white rounded-lg shadow-sm font-bold text-orange-600"
                      type="number"
                    />
                  </div>
                </div>

                <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Preço Licitado (Referência)</label>
                  <v-text-field
                    v-model="dataForm.supply_bidding_price"
                    v-mask-decimal.br="3"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm text-center opacity-70"
                  />
                </div>
              </div>

              <!-- Justificativa (Opcional) -->
              <div class="bg-amber-50 p-4 rounded-xl border border-amber-100">
                <label class="block text-[10px] font-black text-amber-500 uppercase tracking-widest mb-2">Justificativa / Observações</label>
                <v-textarea
                  v-model="dataForm.justificativa"
                  outlined dense hide-details
                  class="bg-white rounded-lg shadow-sm"
                  rows="2"
                  auto-grow
                />
              </div>
            </div>
          </v-form>
          <div v-else class="flex flex-col items-center justify-center py-12">
            <v-progress-circular indeterminate color="orange" size="48"></v-progress-circular>
            <span class="mt-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Carregando dados...</span>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-slate-100 px-8 py-6 flex justify-end gap-3 border-t border-slate-200 shrink-0">
          <button 
            @click="[showModal = false, clearForm()]" 
            class="px-8 py-2 rounded-xl text-slate-500 font-bold uppercase tracking-widest text-[11px] hover:bg-slate-200 transition-colors"
          >
            CANCELAR
          </button>
          <button 
            @click="updateSupplyAuthorization(dataForm)" 
            :disabled="!validForm || loadingSubmit"
            class="px-10 h-12 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-black uppercase tracking-widest text-[11px] shadow-lg shadow-orange-500/20 transition-all transform hover:-translate-y-0.5 flex items-center gap-2"
          >
            <v-icon v-if="loadingSubmit" small class="mdi-spin" color="white">mdi-loading</v-icon>
            <v-icon v-else small color="white">mdi-check</v-icon>
            CONFIRMAR ALTERAÇÃO
          </button>
        </div>
      </div>
    </v-dialog>

    <v-snackbar
      multi-line
      :color="snack.color"
      v-model="snack.show"
      transition="fade-transition"
      top right
    >
      <span class="text-[11px] font-bold uppercase tracking-widest">{{ snack.message }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Date from "@/components/InputDate.vue";
import Helpers from "@/mixins/helpers.js";
import { io } from "socket.io-client";

export default {
  components: { Date },
  mixins: [Helpers],
  data() {
    return {
      socket: null,
      userGroup: null,
      search: "",
      loader: false,
      showModal: false,
      showHistoryModal: false,
      selectedHistoryItem: {},
      modalTitle: "Editar Abastecimento",
      showSearch: false,
      data: [],
      prefectures: [],
      providers: [],
      vehicles: [],
      drivers: [],
      filter: {
        prefecture_id: null,
        provider_id: null,
        vehicle_id: null,
        driver_id: null,
        all_status: true,
        is_autorized: null,
        allow_vehicle_null: true,
        allow_nfe: true,
        startDate: "",
        endDate: "",
        supply_extra: false,
        supply_confered: null,
        exclude_adjusted: true,
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "Data e Hora", value: "datehour", align: "center" },
        { text: "Veículo", value: "model_desc" },
        { text: "Motorista", value: "driver_name" },
        { text: "Liberado por", value: "authorizer" },
        { text: "Fornecedor", value: "provider_name" },
        { text: "Item", value: "item_description_desc" },
        { text: "Lts. Aut.", value: "supply_authorized_liters", align: "center" },
        { text: "Vl. Aut.", value: "supply_authorized_value", align: "center" },
        { text: "Status", value: "supply_status", align: "center" },
        { text: "Lts. Abast.", value: "supply_liters_stocked", align: "center" },
        { text: "Vl. Total", value: "supply_value", align: "center" },
        { text: "Km anterior", value: "last_km", align: "center" },
        { text: "Km atual", value: "supply_km", align: "center" },
        { text: "Média", value: "average", align: "center" },
        { text: "Cupom", value: "supply_coupon", align: "center" },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
      loadingSubmit: false,
      loadingModal: false,
      validForm: false,
      providers_form: [],
      drivers_form: [],
      items_form: [],
      vehicles_form: [],
      fieldRules: [(v) => !!v || "Campo obrigatório"],
      dataForm: {
        supply_id: null,
        prefecture_id: null,
        vehicle_id: null,
        provider_id: null,
        driver_id: null,
        item_id: null,
        supply_authorized_liters: "",
        supply_authorized_value: "",
        supply_price_day: "",
        supply_bidding_price: "",
        supply_liters_stocked: "",
        supply_coupon: "",
        datehour: "",
        supply_status: "",
        supply_km: "",
        supply_value: 0,
        justificativa: "",
      },
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  created() {
    this.initialize();
    this.initSocket();
  },
  beforeDestroy() {
    if (this.socket) {
      this.socket.disconnect();
    }
  },
  computed: {
    disableLitros() {
      return parseFloat(this.dataForm.supply_authorized_value.replace(',', '.')) > 0;
    },
    disableValor() {
      return parseFloat(this.dataForm.supply_authorized_liters.replace(',', '.')) > 0;
    },
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);

      this.filter.prefecture_id = this.$store.getters.getUserData.table_id;
      this.userGroup = this.$store.getters.getUserData.group_id;

      if (this.filter.prefecture_id) {
        this.getVehicles(this.filter.prefecture_id);
        this.getDrivers(this.filter.prefecture_id);
        this.getProviders(this.filter.prefecture_id);
      }

      this.$http.get("/prefectures").then((r) => {
        this.prefectures = r.data;
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      });
    },
    initSocket() {
      let url = (this.$http.defaults.baseURL || "").replace('/api', '').replace('/v1', '').replace(/\/$/, "");
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = window.location.protocol + '//' + window.location.host + (url.startsWith('/') ? url : '/' + url);
      }
      this.socket = io(url || undefined);
      this.socket.on("supply_updated", () => {
        if (!this.showModal && !this.showHistoryModal) {
          this.loadAbastecimento();
        }
      });
    },
    async getVehicles(prefecture_id, is_form = false, only_extra = false) {
      const res = await this.$http.get(`/vehicles/supplies/${prefecture_id}`);
      
      let dataFilter = res.data.filter(v => v.vehicle_active);
      if (only_extra) {
        dataFilter = dataFilter.filter(v => v.abastecimento_extra);
      }

      if (is_form) {
        this.vehicles_form = dataFilter;
      } else {
        this.vehicles = dataFilter;
      }
    },
    async getDrivers(prefecture_id, is_form = false) {
      const res = await this.$http.get(`/supplies/drivers/${prefecture_id}`);

      if (is_form) {
        this.drivers_form = res.data;
      } else {
        this.drivers = res.data;
      }
    },
    async getProviders(prefecture_id, is_form = false) {
      const res = await this.$http.get(`/providers/prefecture/${prefecture_id}`);
      if (is_form) {
        this.providers_form = res.data;
      } else {
        this.providers = res.data;
      }
    },
    loadAbastecimento() {
      if (!this.filter.prefecture_id || !this.filter.provider_id) {
        this.data = [];
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
        return;
      }

      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .put("/supplies/billings", this.filter)
        .then((r) => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    async loadItems(provider_id, vehicle_id, prefecture_id) {
      if (provider_id !== null && prefecture_id !== null && vehicle_id === null) {
        const res = await this.$http.get(`/supplies/providers/prefectures/${provider_id}/${prefecture_id}`);
        this.items_form = res.data;
        return;
      }

      if (provider_id === null || vehicle_id === null || prefecture_id === null) {
        this.items_form = [];
        return;
      }

      const res = await this.$http.get(`/supplies/providers/${provider_id}/${vehicle_id}/${prefecture_id}`);
      this.items_form = res.data;
    },
    clearForm() {
      this.dataForm = {
        supply_id: null,
        prefecture_id: null,
        vehicle_id: null,
        provider_id: null,
        driver_id: null,
        item_id: null,
        supply_authorized_liters: "",
        supply_authorized_value: "",
        supply_price_day: "",
        supply_bidding_price: "",
        supply_liters_stocked: "",
        supply_coupon: "",
        datehour: "",
        supply_status: "",
      };
      this.providers_form = [];
      this.items_form = [];
      this.drivers_form = [];
      this.vehicles_form = [];
      this.validForm = false;
    },
    abrirHistorico(item) {
      this.selectedHistoryItem = item;
      this.showHistoryModal = true;
    },
    getUrlFoto(foto) {
      if (!foto) return null;
      if (typeof foto === 'string' && (foto.startsWith('http://') || foto.startsWith('https://'))) {
        return foto;
      }
      const baseURL = process.env.NODE_ENV === "production"
        ? "https://api.gestaodefrota.net.br/uploads/"
        : `${window.location.protocol}//${window.location.hostname}:3010/uploads/`;
      return baseURL + foto;
    },
    async updateSupplyAuthorization(data) {
      if (this.$refs.validForm.validate()) {
        try {
          this.loadingSubmit = true;

          const payload = { ...data };
          delete payload.supply_id;
          delete payload.supply_status;

          payload.datehour = this.formatarStringData2(payload.datehour);
          payload.supply_authorized_liters = this.convertStringNumber(payload.supply_authorized_liters);
          payload.supply_authorized_value = this.convertStringNumber(payload.supply_authorized_value);
          
          if (data.supply_status === "CONCLUIDO") {
            payload.supply_price_day = this.convertStringNumber(payload.supply_price_day);
            payload.supply_bidding_price = this.convertStringNumber(payload.supply_bidding_price);
            payload.supply_liters_stocked = this.convertStringNumber(payload.supply_liters_stocked);

            payload.supply_value = this.round2(payload.supply_price_day * payload.supply_liters_stocked);
            payload.supply_bidding_value = this.round2(payload.supply_bidding_price * payload.supply_liters_stocked);
          } else {
            delete payload.supply_price_day;
            delete payload.supply_bidding_price;
            delete payload.supply_liters_stocked;
            delete payload.supply_coupon;
          }

          await this.$http.put(`/supplies/single/${data.supply_id}`, { update_simples: true, ...payload })
          this.snack.show = true;
          this.snack.message = "Autorização de abastecimento atualizada com sucesso!";
          this.snack.color = "success";
          this.loadingSubmit = false;
          this.showModal = false;
          this.clearForm();
          this.loadAbastecimento();
        } catch (error) {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = error.message;
          this.loadingSubmit = false;
        }
      }
    },
    abrirFoto(foto) {
      if (!foto) {
        this.snack.message = "Foto não disponível!";
        this.snack.color = "warning";
        this.snack.show = true;
        return;
      }

      const url = this.getUrlFoto(foto);
      window.open(url, "_blank");
    },
    async editar(item) {
      try {
        this.showModal = true;
        this.loadingModal = true;

        this.dataForm = {
          supply_id: item.supply_id,
          prefecture_id: item.prefecture_id,
          vehicle_id: item.vehicle_id,
          provider_id: item.provider_id,
          driver_id: item.driver_id,
          item_id: item.item_id,
          datehour: this.convertDateTime(item.datehour),
          supply_authorized_liters: this.realMaskII(Number(item.supply_authorized_liters || 0).toFixed(3)),
          supply_authorized_value: this.realMask(Number(item.supply_authorized_value || 0).toFixed(2)),
          supply_price_day: this.realMaskII(Number(item.supply_price_day || 0).toFixed(3)),
          supply_bidding_price: this.realMaskII(Number(item.supply_bidding_price || 0).toFixed(3)),
          supply_liters_stocked: this.realMaskII(Number(item.supply_liters_stocked || 0).toFixed(3)),
          supply_coupon: item.supply_coupon ? item.supply_coupon : "",
          supply_status: item.supply_status,
          supply_km: item.supply_km || "",
          supply_value: Number(item.supply_value > 0 ? item.supply_value : (item.supply_liters_stocked * item.supply_price_day) || 0).toFixed(2),
          justificativa: item.justificativa || "",
        };

        await this.getVehicles(item.prefecture_id, true, item.supply_extra);
        await this.getDrivers(item.prefecture_id, true);
        await this.getProviders(item.prefecture_id, true);
        await this.loadItems(item.provider_id, item.vehicle_id, item.prefecture_id);

        this.calculateTotalsForm();

      } catch (error) {
        console.error("Erro ao carregar dados para edição:", error);
        this.snack.show = true;
        this.snack.color = "error";
        this.snack.message = "Erro ao carregar dados do abastecimento.";
      } finally {
        this.loadingModal = false;
      }
    },
    calculateTotalsForm() {
      const price = this.convertStringNumber(this.dataForm.supply_price_day || 0);
      const liters = this.convertStringNumber(this.dataForm.supply_liters_stocked || 0);
      this.dataForm.supply_value = Number(price * liters).toFixed(2);
    },
    async cancelar(item) {
      let c = confirm("Deseja cancelar o abastecimento?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .put(`/supplies/single/${item.supply_id}`, { update_simples: true, supply_status: "CANCELADO" })
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loadAbastecimento();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    }
  },
};
</script>

<style scoped>
.title {
  text-transform: uppercase;
  font-weight: 400;
}
</style>
